import { useState, useEffect, useRef, useCallback } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { useToast } from '../hooks/useToast';

/**
 * useReliableWebSocket
 *
 * A wrapper around react-use-websocket that adds:
 * 1. Queueing of messages when connection is not OPEN.
 * 2. Persistence of the queue to sessionStorage (optional).
 * 3. Automatic flushing of the queue when connection is restored.
 * 4. Offline warning toast when messages are queued but connection is lost.
 *
 * @see {@link https://github.com/robtaussig/react-use-websocket#options react-use-websocket options}
 *
 * @param {string} url - WebSocket URL
 * @param {object} [options] - Options passed to react-use-websocket, plus custom options below.
 * @param {string} [options.persistenceKey] - If provided, queue persists to sessionStorage under this key.
 * @param {function} [options.onConnectionChange] - Callback called with the readyState whenever it changes.
 * @returns {object} - Same interface as useWebSocket
 */
export default function useReliableWebSocket(url, options = {}) {
  // Extract custom options, pass the rest to useWebSocket
  const {
    persistenceKey = null, // If provided, queue persists to sessionStorage
    onConnectionChange,
    ...wsOptions
  } = options;

  // Internal state for the queue
  const [queue, setQueue] = useState(() => {
    if (persistenceKey) {
      try {
        const stored = sessionStorage.getItem(persistenceKey);
        return stored ? JSON.parse(stored) : [];
      } catch (error) {
        console.error("Error loading WS queue:", error);
        return [];
      }
    }
    return [];
  });

  const queueRef = useRef(queue);

  // Sync ref with state
  useEffect(() => {
    queueRef.current = queue;
    if (persistenceKey) {
      sessionStorage.setItem(persistenceKey, JSON.stringify(queue));
    }
  }, [queue, persistenceKey]);

  // Use the library hook
  const {
    sendMessage,
    sendJsonMessage: originalSendJson,
    lastMessage,
    lastJsonMessage,
    readyState,
    getWebSocket,
  } = useWebSocket(url, {
    shouldReconnect: () => true,
    reconnectAttempts: 10,
    reconnectInterval: 3000,
    ...wsOptions,

  });

  // Wrapped sendJsonMessage
  const sendJsonMessage = useCallback((message) => {
    if (readyState === ReadyState.OPEN) {
      originalSendJson(message);
    } else {
      setQueue(prev => [...prev, message]);
    }
  }, [readyState, originalSendJson]);

  const flushQueue = useCallback(() => {
    const currentQueue = queueRef.current;
    if (currentQueue.length === 0) return;

    // Clear queue first. We iterate and send. If send fails (not open), sendJsonMessage re-queues it.
    setQueue([]);

    currentQueue.forEach((msg) => {
      sendJsonMessage(msg);
    });
  }, [sendJsonMessage]);

  // Monitor ReadyState for UI feedback
  useEffect(() => {
    if (onConnectionChange) {
      onConnectionChange(readyState);
    }
  }, [readyState, onConnectionChange]);

  // Queue Flushing Effect
  useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      flushQueue();
    }
  }, [readyState, flushQueue]);

  // Offline Warning Effect
  const { openToast } = useToast();

  useEffect(() => {
    if (readyState === ReadyState.CLOSING || readyState === ReadyState.CLOSED) {
      if (queue.length > 0) {
        openToast("Connection unstable...", 4000, "warning");
      }
    }
  }, [readyState, queue.length, openToast]);

  return {
    sendMessage,
    sendJsonMessage,
    lastMessage,
    lastJsonMessage,
    readyState,
    getWebSocket,
  };
}
